import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ArrowLeft, Info, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { OrderItem } from '@/types/supervisor';
import { getSignedUrlForFile, getUploadMetadata, UploadMetadata } from '@/services/uploadService';
import { getPaperTypes, type PaperType } from '@/services/productService';
import EngineeringDimensionCallouts from '@/components/order/EngineeringDimensionCallouts';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { selectMeasurementUnit, setMeasurementUnit } from '@/features/preferences/preferencesSlice';
import { convertInchesToUnit, convertUnitToInches, unitPrecision } from '@/utils/measurement';
import { type MeasurementUnit, MEASUREMENT_UNITS, translateMeasurementUnitLabel } from '@/types/measurement';
import { analytics } from '@/services/analytics';
import { logging } from '@/services/logging';
import { updateOrderItem } from '@/features/order/orderSlice';
import type { PrintQualityWarning } from '@/services/orderService';
import { calculateCurrentDpi, formatPpi, normalizePrintSizeValue, type PrintSizeValue } from './utils/printSizeForm';

interface SetPrintSizeWindowProps {
  readonly item?: OrderItem;
  readonly onClose: () => void;
  readonly onBack?: () => void;
  readonly onSave: (size: PrintSizeValue) => void;
  readonly saveDisabled?: boolean;
  readonly saveLabel?: string;
  readonly draftSize?: PrintSizeValue;
  readonly onDraftChange?: (size: PrintSizeValue | undefined) => void;
  readonly onDraftReset?: () => void;
  readonly onDraftValidityChange?: (isValid: boolean) => void;
  readonly onRequestPrintQualityHelp?: (warning: PrintQualityWarning) => void;
}

const PRINT_QUALITY_MIN_PPI = 300;
const LOW_PPI_ACKNOWLEDGEMENT_REASON = 'User accepted low PPI print quality warning.';
const DEFAULT_MARGIN_IN = 4 / 25.4;
const DEFAULT_GALLERY_WRAP_IN = 2;
const MIN_MARGIN_PREVIEW_PX = 12;
const MARGIN_PREVIEW_PATTERN = 'repeating-linear-gradient(135deg, rgba(71, 85, 105, 0.18) 0, rgba(71, 85, 105, 0.18) 6px, rgba(255, 255, 255, 0) 6px, rgba(255, 255, 255, 0) 12px)';

const normalizeSides = (width: number, height: number) => ({
  shortSide: Math.min(width, height),
  longSide: Math.max(width, height),
});

const roundDimensionForDisplay = (valueIn: number, unit: MeasurementUnit, precision: number): number => (
  Number(convertInchesToUnit(valueIn, unit).toFixed(precision))
);

const getPaperDimensions = (paper: PaperType): { width: number; height: number } | null => {
  if (
    typeof paper.widthIn !== 'number'
    || typeof paper.lengthIn !== 'number'
    || !Number.isFinite(paper.widthIn)
    || !Number.isFinite(paper.lengthIn)
    || paper.widthIn <= 0
    || paper.lengthIn <= 0
  ) {
    return null;
  }
  return { width: paper.widthIn, height: paper.lengthIn };
};

const deriveDimension = (
  existing?: number,
  metadataDimension?: number,
  pixelValue?: number,
  dpi?: number,
): number | undefined => {
  if (typeof existing === 'number' && existing > 0) {
    return existing;
  }
  if (typeof metadataDimension === 'number' && metadataDimension > 0) {
    return metadataDimension;
  }
  if (
    typeof pixelValue === 'number'
    && pixelValue > 0
    && typeof dpi === 'number'
    && dpi > 0
  ) {
    return pixelValue / dpi;
  }
  return undefined;
};

const clampTwoDecimals = (value?: number): number | undefined => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return undefined;
  }
  return Math.round(value * 100) / 100;
};

const SetPrintSizeWindow: React.FC<SetPrintSizeWindowProps> = ({
  item: initialItem,
  onClose,
  onBack,
  onSave,
  saveDisabled = false,
  saveLabel,
  draftSize,
  onDraftChange,
  onDraftReset,
  onDraftValidityChange,
  onRequestPrintQualityHelp,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const measurementUnit = useAppSelector(selectMeasurementUnit);
  const targetItemKey = initialItem?.id ?? null;
  const liveItem = useAppSelector(state => {
    if (!targetItemKey) {
      return undefined;
    }
    return state.order.items?.find(current => current.id === targetItemKey);
  });
  const item = liveItem ?? initialItem;
  const precision = unitPrecision(measurementUnit);
  const unitLabel = useMemo(() => translateMeasurementUnitLabel(t, measurementUnit), [measurementUnit, t]);
  const inputStep = useMemo(() => {
    switch (measurementUnit) {
      case 'mm':
        return '1';
      case 'cm':
        return '0.1';
      default:
        return '0.01';
    }
  }, [measurementUnit]);

  const inputMin = useMemo(() => {
    switch (measurementUnit) {
      case 'mm':
        return '1';
      case 'cm':
        return '0.1';
      default:
        return '0.01';
    }
  }, [measurementUnit]);

  const [metadata, setMetadata] = useState<UploadMetadata | null>(null);
  const [paperTypes, setPaperTypes] = useState<PaperType[]>([]);
  const [imageSrc, setImageSrc] = useState<string | undefined>();
  const [widthIn, setWidthIn] = useState<number | undefined>();
  const [heightIn, setHeightIn] = useState<number | undefined>();
  const [marginIn, setMarginIn] = useState<number | undefined>();
  const [galleryWrapIn, setGalleryWrapIn] = useState<number | undefined>();
  const [galleryWrapMirrorEdges, setGalleryWrapMirrorEdges] = useState(false);
  const [widthInputValue, setWidthInputValue] = useState('');
  const [heightInputValue, setHeightInputValue] = useState('');
  const [marginInputValue, setMarginInputValue] = useState('');
  const [galleryWrapInputValue, setGalleryWrapInputValue] = useState('');
  const [isWidthInputFocused, setIsWidthInputFocused] = useState(false);
  const [isHeightInputFocused, setIsHeightInputFocused] = useState(false);
  const [isMarginInputFocused, setIsMarginInputFocused] = useState(false);
  const [isGalleryWrapInputFocused, setIsGalleryWrapInputFocused] = useState(false);
  const [isMarginEnabled, setIsMarginEnabled] = useState(false);
  const [isGalleryWrapEnabled, setIsGalleryWrapEnabled] = useState(false);
  const [aspectRatio, setAspectRatio] = useState<number | null>(null);
  const [baseDimensions, setBaseDimensions] = useState<{ width: number; height: number } | null>(null);
  const [previewScale, setPreviewScale] = useState(0.8);
  const previewViewportRef = useRef<HTMLDivElement | null>(null);
  const previewCanvasRef = useRef<HTMLDivElement | null>(null);
  const previewRasterRef = useRef<HTMLCanvasElement | null>(null);
  const [canvasSize, setCanvasSize] = useState<{ width: number; height: number } | null>(null);
  const [dimensionBounds, setDimensionBounds] = useState<{
    left: number;
    right: number;
    top: number;
    bottom: number;
    viewportWidth: number;
    viewportHeight: number;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingQualityAction, setPendingQualityAction] = useState<'save' | null>(null);
  const initialSizeRef = useRef<{
    key: string;
    widthIn: number;
    heightIn: number;
    marginIn?: number | null;
    galleryWrapIn?: number | null;
    galleryWrapMirrorEdges?: boolean | null;
  } | null>(null);
  const didSaveRef = useRef(false);
  const originalAspectRatioRef = useRef<number | null>(null);
  const hydrationKeyRef = useRef<string | null>(null);

  const draftChangeHandler: ((size: PrintSizeValue | undefined) => void) | null =
    typeof onDraftChange === 'function' ? onDraftChange : null;
  const isDraftControlled = draftChangeHandler !== null;

  const emitDraftChange = useCallback((next: PrintSizeValue | undefined) => {
    if (draftChangeHandler) {
      draftChangeHandler(next);
    }
  }, [draftChangeHandler]);

  const resetState = useCallback(() => {
    setMetadata(null);
    setImageSrc(undefined);
    setWidthIn(undefined);
    setHeightIn(undefined);
    setMarginIn(undefined);
    setGalleryWrapIn(undefined);
    setGalleryWrapMirrorEdges(false);
    setWidthInputValue('');
    setHeightInputValue('');
    setMarginInputValue('');
    setGalleryWrapInputValue('');
    setIsWidthInputFocused(false);
    setIsHeightInputFocused(false);
    setIsMarginInputFocused(false);
    setIsGalleryWrapInputFocused(false);
    setIsMarginEnabled(false);
    setIsGalleryWrapEnabled(false);
    setAspectRatio(null);
    setBaseDimensions(null);
    setPreviewScale(0.8);
    setCanvasSize(null);
    setError(null);
    originalAspectRatioRef.current = null;
    hydrationKeyRef.current = null;
  }, []);

  useEffect(() => {
    if (!item) {
      return;
    }

    const cancellation = { current: false };

    const run = async () => {
      try {
        const papers = await getPaperTypes();
        if (!cancellation.current) {
          setPaperTypes(papers);
        }
      } catch (err) {
        if (!cancellation.current) {
          logging.error('Failed to load paper types for print size validation', { error: err });
          setPaperTypes([]);
        }
      }
    };

    void run();

    return () => {
      cancellation.current = true;
    };
  }, [item?.id]);

  const applyDraftSize = useCallback((size?: PrintSizeValue) => {
    if (!size) {
      return;
    }
    const { widthIn: draftWidth, heightIn: draftHeight } = size;
    if (typeof draftWidth === 'number' && draftWidth > 0) {
      setWidthIn(draftWidth);
    }
    if (typeof draftHeight === 'number' && draftHeight > 0) {
      setHeightIn(draftHeight);
    }
    if (typeof draftWidth === 'number' && draftWidth > 0 && typeof draftHeight === 'number' && draftHeight > 0) {
      setAspectRatio(draftWidth / draftHeight);
    }
    if (typeof size.marginIn === 'number' && size.marginIn > 0) {
      setMarginIn(size.marginIn);
      setIsMarginEnabled(true);
      setGalleryWrapIn(undefined);
      setIsGalleryWrapEnabled(false);
      setGalleryWrapMirrorEdges(false);
    } else if (typeof size.galleryWrapIn === 'number' && size.galleryWrapIn > 0) {
      setGalleryWrapIn(size.galleryWrapIn);
      setIsGalleryWrapEnabled(true);
      setMarginIn(undefined);
      setIsMarginEnabled(false);
      setGalleryWrapMirrorEdges(size.galleryWrapMirrorEdges === true);
    } else {
      setMarginIn(undefined);
      setIsMarginEnabled(false);
      setGalleryWrapIn(undefined);
      setIsGalleryWrapEnabled(false);
      setGalleryWrapMirrorEdges(false);
    }
  }, []);

  const revertToInitialSize = useCallback(() => {
    if (didSaveRef.current) {
      return;
    }
    const snapshot = initialSizeRef.current;
    if (!snapshot) {
      return;
    }

    setWidthIn(snapshot.widthIn);
    setHeightIn(snapshot.heightIn);
    setAspectRatio(snapshot.widthIn > 0 && snapshot.heightIn > 0 ? snapshot.widthIn / snapshot.heightIn : null);
    setWidthInputValue(convertInchesToUnit(snapshot.widthIn, measurementUnit).toFixed(precision));
    setHeightInputValue(convertInchesToUnit(snapshot.heightIn, measurementUnit).toFixed(precision));
    if (typeof snapshot.marginIn === 'number' && snapshot.marginIn > 0) {
      setMarginIn(snapshot.marginIn);
      setIsMarginEnabled(true);
      setMarginInputValue(convertInchesToUnit(snapshot.marginIn, measurementUnit).toFixed(precision));
      setGalleryWrapIn(undefined);
      setIsGalleryWrapEnabled(false);
      setGalleryWrapInputValue('');
      setGalleryWrapMirrorEdges(false);
    } else if (typeof snapshot.galleryWrapIn === 'number' && snapshot.galleryWrapIn > 0) {
      setGalleryWrapIn(snapshot.galleryWrapIn);
      setIsGalleryWrapEnabled(true);
      setGalleryWrapInputValue(convertInchesToUnit(snapshot.galleryWrapIn, measurementUnit).toFixed(precision));
      setMarginIn(undefined);
      setIsMarginEnabled(false);
      setMarginInputValue('');
      setGalleryWrapMirrorEdges(snapshot.galleryWrapMirrorEdges === true);
    } else {
      setMarginIn(undefined);
      setIsMarginEnabled(false);
      setMarginInputValue('');
      setGalleryWrapIn(undefined);
      setIsGalleryWrapEnabled(false);
      setGalleryWrapInputValue('');
      setGalleryWrapMirrorEdges(false);
    }
  }, [measurementUnit, precision]);

  useEffect(() => {
    resetState();
    const cancellation = { current: false };
    setIsLoading(true);
    setError(null);

    const run = async () => {
      try {
        if (!item?.uploadedFileId) {
          setMetadata(null);
          setImageSrc(undefined);
          return;
        }

        const [meta, signed] = await Promise.all([
          getUploadMetadata(item.uploadedFileId),
          getSignedUrlForFile(item.uploadedFileId),
        ]);
        if (cancellation.current) {
          return;
        }
        setMetadata(meta);
        setImageSrc(signed.thumbnailSignedUrl ?? signed.signedUrl);
      } catch (err) {
        if (cancellation.current) {
          return;
        }
        logging.error('Failed to load image metadata', { error: err });
        setError(t('setPrintSize.loadError', 'Unable to load image details.'));
      } finally {
        if (!cancellation.current) {
          setIsLoading(false);
        }
      }
    };

    run();

    return () => {
      cancellation.current = true;
      setIsLoading(false);
    };
  }, [item?.uploadedFileId, resetState, t]);

  useEffect(() => {
    if (!isDraftControlled) {
      return;
    }
    applyDraftSize(draftSize);
  }, [applyDraftSize, draftSize, isDraftControlled]);

  useEffect(() => {
    if (!item) {
      return;
    }

    const hasDraftDimensions = isDraftControlled
      && typeof draftSize?.widthIn === 'number'
      && draftSize.widthIn > 0
      && typeof draftSize?.heightIn === 'number'
      && draftSize.heightIn > 0;

    const derivedWidth = clampTwoDecimals(
      deriveDimension(
        item.widthIn ?? undefined,
        metadata?.widthIn,
        metadata?.width,
        metadata?.dpiX,
      ),
    );
    const derivedHeight = clampTwoDecimals(
      deriveDimension(
        item.heightIn ?? undefined,
        metadata?.heightIn,
        metadata?.height,
        metadata?.dpiY,
      ),
    );

    const derivedMargin = clampTwoDecimals(item.marginIn ?? undefined);
    const derivedGalleryWrap = clampTwoDecimals(item.galleryWrapIn ?? undefined);
    const derivedGalleryWrapMirrorEdges = item.galleryWrapMirrorEdges === true;
    const hasDerivedWidth = typeof derivedWidth === 'number' && derivedWidth > 0;
    const hasDerivedHeight = typeof derivedHeight === 'number' && derivedHeight > 0;
    const hasDerivedMargin = typeof derivedMargin === 'number' && derivedMargin > 0;
    const hasDerivedGalleryWrap = !hasDerivedMargin && typeof derivedGalleryWrap === 'number' && derivedGalleryWrap > 0;

    if (!hasDerivedWidth || !hasDerivedHeight) {
      return;
    }

    const nextHydrationKey = [
      item.id ?? '',
      metadata?.id ?? 'no-meta',
      hasDerivedWidth ? derivedWidth : 'no-width',
      hasDerivedHeight ? derivedHeight : 'no-height',
      hasDerivedMargin ? derivedMargin : 'no-margin',
      hasDerivedGalleryWrap ? derivedGalleryWrap : 'no-gallery-wrap',
      hasDerivedGalleryWrap ? String(derivedGalleryWrapMirrorEdges) : 'no-gallery-wrap-mirror',
    ].join('|');

    const isFirstHydration = hydrationKeyRef.current !== nextHydrationKey;
    hydrationKeyRef.current = nextHydrationKey;

    if (isFirstHydration) {
      if (!hasDraftDimensions) {
        setWidthIn(derivedWidth);
        setHeightIn(derivedHeight);
        setMarginIn(hasDerivedMargin ? derivedMargin : undefined);
        setIsMarginEnabled(hasDerivedMargin);
        setGalleryWrapIn(hasDerivedGalleryWrap ? derivedGalleryWrap : undefined);
        setIsGalleryWrapEnabled(hasDerivedGalleryWrap);
        setGalleryWrapMirrorEdges(hasDerivedGalleryWrap ? derivedGalleryWrapMirrorEdges : false);
      }
    } else if (!hasDraftDimensions) {
      setWidthIn(current => (typeof current === 'number' && current > 0 ? current : derivedWidth));
      setHeightIn(current => (typeof current === 'number' && current > 0 ? current : derivedHeight));
    }

    const derivedRatio = derivedWidth / derivedHeight;
    if (derivedRatio > 0) {
      if (originalAspectRatioRef.current == null || originalAspectRatioRef.current <= 0) {
        originalAspectRatioRef.current = derivedRatio;
      }
      setAspectRatio(prev => (prev ?? derivedRatio));
    }

    if (
      isFirstHydration
      || !baseDimensions
      || baseDimensions.width <= 0
      || baseDimensions.height <= 0
    ) {
      setBaseDimensions({ width: derivedWidth, height: derivedHeight });
      setPreviewScale(0.8);
    }

    const currentKey = item.id ?? '';
    if (initialSizeRef.current && initialSizeRef.current.key !== currentKey) {
      initialSizeRef.current = null;
      didSaveRef.current = false;
    }

    if (!initialSizeRef.current || isFirstHydration) {
      initialSizeRef.current = {
        key: currentKey,
        widthIn: derivedWidth,
        heightIn: derivedHeight,
        marginIn: hasDerivedMargin ? derivedMargin : undefined,
        galleryWrapIn: hasDerivedGalleryWrap ? derivedGalleryWrap : undefined,
        galleryWrapMirrorEdges: hasDerivedGalleryWrap ? derivedGalleryWrapMirrorEdges : false,
      };
    }
  }, [baseDimensions, draftSize, isDraftControlled, item, metadata]);

  useEffect(() => {
    if (!baseDimensions) {
      return;
    }
    if (typeof widthIn !== 'number' || typeof heightIn !== 'number' || widthIn <= 0 || heightIn <= 0) {
      return;
    }

    const scaleFactor = widthIn / baseDimensions.width;
    if (!Number.isFinite(scaleFactor) || scaleFactor <= 0) {
      return;
    }

    const expandedScale = 0.8 * scaleFactor;
    const computedScale = Math.min(expandedScale, 1);

    if (computedScale >= 0.999 && scaleFactor > 1.001) {
      setBaseDimensions({ width: widthIn, height: heightIn });
      setPreviewScale(0.8);
      setAspectRatio(widthIn / heightIn);
      return;
    }

    if (computedScale <= 0.6) {
      setBaseDimensions({ width: widthIn, height: heightIn });
      setPreviewScale(0.8);
      setAspectRatio(widthIn / heightIn);
      return;
    }

    const clampedScale = Math.max(computedScale, 0.6);
    setPreviewScale(clampedScale);
  }, [baseDimensions, heightIn, widthIn]);

  useEffect(() => {
    const viewportEl = previewViewportRef.current;
    const canvasEl = previewCanvasRef.current;

    if (!viewportEl || !canvasEl) {
      setDimensionBounds(null);
      return;
    }

    const computeBounds = () => {
      const viewportRect = viewportEl.getBoundingClientRect();
      const canvasRect = canvasEl.getBoundingClientRect();

      if (
        viewportRect.width === 0
        || viewportRect.height === 0
        || canvasRect.width === 0
        || canvasRect.height === 0
      ) {
        setDimensionBounds(null);
        return;
      }

      const clampWithin = (value: number, max: number) => Math.max(0, Math.min(max, value));
      const viewportWidth = viewportRect.width;
      const viewportHeight = viewportRect.height;
      const left = clampWithin(canvasRect.left - viewportRect.left, viewportWidth);
      const right = clampWithin(canvasRect.right - viewportRect.left, viewportWidth);
      const top = clampWithin(canvasRect.top - viewportRect.top, viewportHeight);
      const bottom = clampWithin(canvasRect.bottom - viewportRect.top, viewportHeight);

      setDimensionBounds({
        left,
        right,
        top,
        bottom,
        viewportWidth,
        viewportHeight,
      });
    };

    const observer = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => {
        computeBounds();
      })
      : null;
    if (observer) {
      observer.observe(viewportEl);
      observer.observe(canvasEl);
    }

    computeBounds();

    return () => {
      observer?.disconnect();
    };
  }, [canvasSize, heightIn, imageSrc, previewScale, widthIn]);

  useEffect(() => {
    const viewportEl = previewViewportRef.current;
    if (!viewportEl) {
      setCanvasSize(null);
      return;
    }
    if (typeof widthIn !== 'number' || typeof heightIn !== 'number' || widthIn <= 0 || heightIn <= 0) {
      setCanvasSize(null);
      return;
    }

    const computeCanvasSize = () => {
      const viewportRect = viewportEl.getBoundingClientRect();
      if (viewportRect.width === 0 || viewportRect.height === 0) {
        setCanvasSize(null);
        return;
      }

      const totalCanvasWidthIn = widthIn + (isGalleryWrapEnabled && typeof galleryWrapIn === 'number' && galleryWrapIn > 0 ? galleryWrapIn * 2 : 0);
      const totalCanvasHeightIn = heightIn + (isGalleryWrapEnabled && typeof galleryWrapIn === 'number' && galleryWrapIn > 0 ? galleryWrapIn * 2 : 0);
      const aspect = totalCanvasWidthIn / totalCanvasHeightIn;
      const maxWidth = viewportRect.width * 0.92;
      const maxHeight = viewportRect.height * 0.92;
      const sizeByWidth = {
        width: maxWidth,
        height: maxWidth / aspect,
      };
      const fittedSize = sizeByWidth.height > maxHeight
        ? {
            width: maxHeight * aspect,
            height: maxHeight,
          }
        : sizeByWidth;
      const nextWidth = fittedSize.width * previewScale;
      const nextHeight = fittedSize.height * previewScale;

      setCanvasSize({
        width: Math.max(1, nextWidth),
        height: Math.max(1, nextHeight),
      });
    };

    const observer = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => {
        computeCanvasSize();
      })
      : null;
    if (observer) {
      observer.observe(viewportEl);
    }

    computeCanvasSize();

    return () => {
      observer?.disconnect();
    };
  }, [galleryWrapIn, heightIn, imageSrc, isGalleryWrapEnabled, previewScale, widthIn]);

  const formatDimension = (value?: number) => {
    if (typeof value !== 'number') {
      return '—';
    }
    const converted = convertInchesToUnit(value, measurementUnit);
    return converted.toFixed(precision);
  };

  const marginLabel = useMemo(() => {
    if (!isMarginEnabled || typeof marginIn !== 'number' || marginIn <= 0) {
      return null;
    }
    return `${formatDimension(marginIn)} ${unitLabel}`;
  }, [formatDimension, isMarginEnabled, marginIn, unitLabel]);

  const galleryWrapLabel = useMemo(() => {
    if (!isGalleryWrapEnabled || typeof galleryWrapIn !== 'number' || galleryWrapIn <= 0) {
      return null;
    }
    return `${formatDimension(galleryWrapIn)} ${unitLabel}`;
  }, [formatDimension, galleryWrapIn, isGalleryWrapEnabled, unitLabel]);

  const galleryWrapBillableIncreaseLabel = useMemo(() => {
    if (!isGalleryWrapEnabled || typeof galleryWrapIn !== 'number' || galleryWrapIn <= 0) {
      return null;
    }
    return `+${formatDimension(galleryWrapIn * 2)}`;
  }, [formatDimension, galleryWrapIn, isGalleryWrapEnabled]);

  const totalCanvasDimensions = useMemo(() => {
    if (typeof widthIn !== 'number' || typeof heightIn !== 'number' || widthIn <= 0 || heightIn <= 0) {
      return { widthIn, heightIn };
    }
    if (isGalleryWrapEnabled && typeof galleryWrapIn === 'number' && galleryWrapIn > 0) {
      return {
        widthIn: widthIn + galleryWrapIn * 2,
        heightIn: heightIn + galleryWrapIn * 2,
      };
    }
    return { widthIn, heightIn };
  }, [galleryWrapIn, heightIn, isGalleryWrapEnabled, widthIn]);

  const sizeLimitValidation = useMemo(() => {
    const totalWidthIn = totalCanvasDimensions.widthIn;
    const totalHeightIn = totalCanvasDimensions.heightIn;
    if (
      typeof totalWidthIn !== 'number'
      || typeof totalHeightIn !== 'number'
      || totalWidthIn <= 0
      || totalHeightIn <= 0
    ) {
      return null;
    }

    const hasSelectedPaper = Boolean(item?.paperTypeId);
    const selectedPaper = item?.paperTypeId
      ? paperTypes.find(paper => paper.id === item.paperTypeId)
      : undefined;
    const selectedDimensions = selectedPaper ? getPaperDimensions(selectedPaper) : null;
    const selectedLimit = selectedDimensions
      ? normalizeSides(selectedDimensions.width, selectedDimensions.height)
      : null;
    const fallbackLimit = !hasSelectedPaper
      ? paperTypes
        .filter(paper => paper.available !== false)
        .reduce<{ shortSide: number; longSide: number } | null>((current, paper) => {
          const dimensions = getPaperDimensions(paper);
          if (!dimensions) {
            return current;
          }
          const normalized = normalizeSides(dimensions.width, dimensions.height);
          return {
            shortSide: Math.max(current?.shortSide ?? 0, normalized.shortSide),
            longSide: Math.max(current?.longSide ?? 0, normalized.longSide),
          };
        }, null)
      : null;
    const limit = selectedLimit ?? fallbackLimit;

    if (!limit || limit.shortSide <= 0 || limit.longSide <= 0) {
      return null;
    }

    const totalCanvas = normalizeSides(totalWidthIn, totalHeightIn);
    const displayedShortSide = roundDimensionForDisplay(totalCanvas.shortSide, measurementUnit, precision);
    const displayedLongSide = roundDimensionForDisplay(totalCanvas.longSide, measurementUnit, precision);
    const displayedLimitShortSide = roundDimensionForDisplay(limit.shortSide, measurementUnit, precision);
    const displayedLimitLongSide = roundDimensionForDisplay(limit.longSide, measurementUnit, precision);
    const isValid = displayedShortSide <= displayedLimitShortSide && displayedLongSide <= displayedLimitLongSide;

    if (isValid) {
      return null;
    }

    const currentSize = `${formatDimension(totalCanvas.shortSide)} × ${formatDimension(totalCanvas.longSide)} ${unitLabel}`;
    const maxSize = `${formatDimension(limit.shortSide)} × ${formatDimension(limit.longSide)} ${unitLabel}`;
    return {
      maxShortSideIn: limit.shortSide,
      maxLongSideIn: limit.longSide,
      message: selectedLimit
        ? t(
          'setPrintSize.selectedPaperSizeLimit',
          'Your size is {{currentSize}} now. Selected paper supports up to {{maxSize}}.',
          { currentSize, maxSize },
        )
        : t(
          'setPrintSize.maxPaperSizeLimit',
          'Your size is {{currentSize}} now. Maximum supported paper size is {{maxSize}}.',
          { currentSize, maxSize },
        ),
    };
  }, [formatDimension, item?.paperTypeId, measurementUnit, paperTypes, precision, t, totalCanvasDimensions.heightIn, totalCanvasDimensions.widthIn, unitLabel]);

  useEffect(() => {
    onDraftValidityChange?.(!sizeLimitValidation);
  }, [onDraftValidityChange, sizeLimitValidation]);

  const previewMetrics = useMemo(() => {
    if (!canvasSize || typeof widthIn !== 'number' || typeof heightIn !== 'number' || widthIn <= 0 || heightIn <= 0) {
      return null;
    }
    const wideSideIn = Math.max(widthIn, heightIn);
    const wideSidePx = Math.max(canvasSize.width, canvasSize.height);
    const isMarginActive = isMarginEnabled && typeof marginIn === 'number' && marginIn > 0;
    const isGalleryWrapActive = isGalleryWrapEnabled && typeof galleryWrapIn === 'number' && galleryWrapIn > 0;
    const edgeMetrics = (() => {
      const maxMarginPx = Math.min(canvasSize.width, canvasSize.height) / 2;

      if (isMarginActive && wideSideIn > 0 && wideSidePx > 0) {
        const rawMarginPx = (marginIn / wideSideIn) * wideSidePx;
        const marginPx = Math.min(Math.max(rawMarginPx, MIN_MARGIN_PREVIEW_PX), maxMarginPx);
        const availableWideSide = wideSidePx - 2 * marginPx;
        const rawScaleFactor = availableWideSide / wideSidePx;
        const scaleFactor = Number.isFinite(rawScaleFactor)
          ? Math.min(Math.max(rawScaleFactor, 0.05), 1)
          : 1;
        return {
          artworkWidth: canvasSize.width * scaleFactor,
          artworkHeight: canvasSize.height * scaleFactor,
          marginPx,
        };
      }

      if (isGalleryWrapActive) {
        const totalWidthIn = totalCanvasDimensions.widthIn ?? widthIn;
        const totalHeightIn = totalCanvasDimensions.heightIn ?? heightIn;
        const totalWideSideIn = Math.max(totalWidthIn, totalHeightIn);
        const rawMarginPx = totalWideSideIn > 0 && wideSidePx > 0
          ? (galleryWrapIn / totalWideSideIn) * wideSidePx
          : 0;
        const marginPx = Math.min(Math.max(rawMarginPx, MIN_MARGIN_PREVIEW_PX), maxMarginPx);
        return {
          artworkWidth: Math.max(1, canvasSize.width - marginPx * 2),
          artworkHeight: Math.max(1, canvasSize.height - marginPx * 2),
          marginPx,
        };
      }

      return {
        artworkWidth: canvasSize.width,
        artworkHeight: canvasSize.height,
        marginPx: 0,
      };
    })();
    const { artworkWidth, artworkHeight, marginPx } = edgeMetrics;
    const offsetX = (canvasSize.width - artworkWidth) / 2;
    const offsetY = (canvasSize.height - artworkHeight) / 2;
    return {
      canvasWidth: canvasSize.width,
      canvasHeight: canvasSize.height,
      artworkWidth,
      artworkHeight,
      offsetX,
      offsetY,
      isMarginActive,
      isGalleryWrapActive,
      marginPx,
    };
  }, [canvasSize, galleryWrapIn, heightIn, isGalleryWrapEnabled, isMarginEnabled, marginIn, totalCanvasDimensions.heightIn, totalCanvasDimensions.widthIn, widthIn]);

  const isMirroredGalleryWrapPreview = useMemo(
    () => previewMetrics?.isGalleryWrapActive === true && galleryWrapMirrorEdges && Boolean(imageSrc),
    [galleryWrapMirrorEdges, imageSrc, previewMetrics],
  );

  useEffect(() => {
    const canvas = previewRasterRef.current;
    if (!canvas || !previewMetrics || !imageSrc || !isMirroredGalleryWrapPreview) {
      if (canvas) {
        const context = canvas.getContext('2d');
        context?.clearRect(0, 0, canvas.width, canvas.height);
      }
      return;
    }

    const devicePixelRatio = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
    const targetWidth = Math.max(1, Math.round(previewMetrics.canvasWidth * devicePixelRatio));
    const targetHeight = Math.max(1, Math.round(previewMetrics.canvasHeight * devicePixelRatio));

    if (canvas.width !== targetWidth || canvas.height !== targetHeight) {
      canvas.width = targetWidth;
      canvas.height = targetHeight;
    }

    canvas.style.width = `${previewMetrics.canvasWidth}px`;
    canvas.style.height = `${previewMetrics.canvasHeight}px`;

    const context = canvas.getContext('2d');
    if (!context) {
      return;
    }

    const image = new Image();
    image.decoding = 'async';

    const drawPreview = () => {
      const canvasWidth = previewMetrics.canvasWidth;
      const canvasHeight = previewMetrics.canvasHeight;
      const artworkWidth = previewMetrics.artworkWidth;
      const artworkHeight = previewMetrics.artworkHeight;
      const wrapLeftPx = previewMetrics.offsetX;
      const wrapTopPx = previewMetrics.offsetY;
      const wrapRightPx = previewMetrics.canvasWidth - previewMetrics.offsetX - previewMetrics.artworkWidth;
      const wrapBottomPx = previewMetrics.canvasHeight - previewMetrics.offsetY - previewMetrics.artworkHeight;

      const sourceEdgeWidth = Math.max(1, Math.min(
        image.naturalWidth,
        Math.round((Math.max(wrapLeftPx, wrapRightPx) / artworkWidth) * image.naturalWidth),
      ));
      const sourceEdgeHeight = Math.max(1, Math.min(
        image.naturalHeight,
        Math.round((Math.max(wrapTopPx, wrapBottomPx) / artworkHeight) * image.naturalHeight),
      ));

      context.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0);
      context.clearRect(0, 0, canvasWidth, canvasHeight);
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, canvasWidth, canvasHeight);

      const drawMirroredSection = ({
        destinationX,
        destinationY,
        destinationWidth,
        destinationHeight,
        sourceX,
        sourceY,
        sourceWidth,
        sourceHeight,
        flipX,
        flipY,
      }: {
        destinationX: number;
        destinationY: number;
        destinationWidth: number;
        destinationHeight: number;
        sourceX: number;
        sourceY: number;
        sourceWidth: number;
        sourceHeight: number;
        flipX?: boolean;
        flipY?: boolean;
      }) => {
        if (destinationWidth <= 0 || destinationHeight <= 0 || sourceWidth <= 0 || sourceHeight <= 0) {
          return;
        }
        context.save();
        context.translate(
          destinationX + (flipX ? destinationWidth : 0),
          destinationY + (flipY ? destinationHeight : 0),
        );
        context.scale(flipX ? -1 : 1, flipY ? -1 : 1);
        context.drawImage(
          image,
          sourceX,
          sourceY,
          sourceWidth,
          sourceHeight,
          0,
          0,
          destinationWidth,
          destinationHeight,
        );
        context.restore();
      };

      drawMirroredSection({
        destinationX: wrapLeftPx,
        destinationY: 0,
        destinationWidth: artworkWidth,
        destinationHeight: wrapTopPx,
        sourceX: 0,
        sourceY: 0,
        sourceWidth: image.naturalWidth,
        sourceHeight: sourceEdgeHeight,
        flipY: true,
      });
      drawMirroredSection({
        destinationX: wrapLeftPx,
        destinationY: wrapTopPx + artworkHeight,
        destinationWidth: artworkWidth,
        destinationHeight: wrapBottomPx,
        sourceX: 0,
        sourceY: Math.max(0, image.naturalHeight - sourceEdgeHeight),
        sourceWidth: image.naturalWidth,
        sourceHeight: sourceEdgeHeight,
        flipY: true,
      });
      drawMirroredSection({
        destinationX: 0,
        destinationY: wrapTopPx,
        destinationWidth: wrapLeftPx,
        destinationHeight: artworkHeight,
        sourceX: 0,
        sourceY: 0,
        sourceWidth: sourceEdgeWidth,
        sourceHeight: image.naturalHeight,
        flipX: true,
      });
      drawMirroredSection({
        destinationX: wrapLeftPx + artworkWidth,
        destinationY: wrapTopPx,
        destinationWidth: wrapRightPx,
        destinationHeight: artworkHeight,
        sourceX: Math.max(0, image.naturalWidth - sourceEdgeWidth),
        sourceY: 0,
        sourceWidth: sourceEdgeWidth,
        sourceHeight: image.naturalHeight,
        flipX: true,
      });
      drawMirroredSection({
        destinationX: 0,
        destinationY: 0,
        destinationWidth: wrapLeftPx,
        destinationHeight: wrapTopPx,
        sourceX: 0,
        sourceY: 0,
        sourceWidth: sourceEdgeWidth,
        sourceHeight: sourceEdgeHeight,
        flipX: true,
        flipY: true,
      });
      drawMirroredSection({
        destinationX: wrapLeftPx + artworkWidth,
        destinationY: 0,
        destinationWidth: wrapRightPx,
        destinationHeight: wrapTopPx,
        sourceX: Math.max(0, image.naturalWidth - sourceEdgeWidth),
        sourceY: 0,
        sourceWidth: sourceEdgeWidth,
        sourceHeight: sourceEdgeHeight,
        flipX: true,
        flipY: true,
      });
      drawMirroredSection({
        destinationX: 0,
        destinationY: wrapTopPx + artworkHeight,
        destinationWidth: wrapLeftPx,
        destinationHeight: wrapBottomPx,
        sourceX: 0,
        sourceY: Math.max(0, image.naturalHeight - sourceEdgeHeight),
        sourceWidth: sourceEdgeWidth,
        sourceHeight: sourceEdgeHeight,
        flipX: true,
        flipY: true,
      });
      drawMirroredSection({
        destinationX: wrapLeftPx + artworkWidth,
        destinationY: wrapTopPx + artworkHeight,
        destinationWidth: wrapRightPx,
        destinationHeight: wrapBottomPx,
        sourceX: Math.max(0, image.naturalWidth - sourceEdgeWidth),
        sourceY: Math.max(0, image.naturalHeight - sourceEdgeHeight),
        sourceWidth: sourceEdgeWidth,
        sourceHeight: sourceEdgeHeight,
        flipX: true,
        flipY: true,
      });

      context.drawImage(
        image,
        0,
        0,
        image.naturalWidth,
        image.naturalHeight,
        wrapLeftPx,
        wrapTopPx,
        artworkWidth,
        artworkHeight,
      );
      context.strokeStyle = 'rgba(71, 85, 105, 0.45)';
      context.lineWidth = 1;
      context.strokeRect(wrapLeftPx + 0.5, wrapTopPx + 0.5, artworkWidth - 1, artworkHeight - 1);
    };

    image.onload = drawPreview;
    image.src = imageSrc;
  }, [imageSrc, isMirroredGalleryWrapPreview, previewMetrics]);

  const formatOriginalSize = () => {
    const sourceWidth = clampTwoDecimals(metadata?.widthIn ?? item?.widthIn ?? widthIn);
    const sourceHeight = clampTwoDecimals(metadata?.heightIn ?? item?.heightIn ?? heightIn);
    if (sourceWidth && sourceHeight) {
      return `${formatDimension(sourceWidth)} ${unitLabel} × ${formatDimension(sourceHeight)} ${unitLabel}`;
    }
    if (
      metadata?.width
      && metadata?.height
      && metadata?.dpiX
      && metadata?.dpiY
      && metadata.dpiX > 0
      && metadata.dpiY > 0
    ) {
      return `${formatDimension(metadata.width / metadata.dpiX)} ${unitLabel} × ${formatDimension(metadata.height / metadata.dpiY)} ${unitLabel}`;
    }
    return '—';
  };

  const resolveAspectRatio = useCallback((): number | null => {
    const lockedRatio = originalAspectRatioRef.current;
    if (lockedRatio && lockedRatio > 0) {
      return lockedRatio;
    }
    if (aspectRatio && aspectRatio > 0) {
      return aspectRatio;
    }
    if (widthIn && heightIn && heightIn > 0) {
      return widthIn / heightIn;
    }
    return null;
  }, [aspectRatio, heightIn, widthIn]);

  const syncDraftSize = useCallback(
    (
      width: number | undefined,
      height: number | undefined,
      margin?: number | null,
      galleryWrap?: number | null,
      mirrorEdges?: boolean | null,
    ) => {
      if (!draftChangeHandler) {
        return;
      }

      const nextValue = normalizePrintSizeValue(
        typeof width === 'number' && width > 0 && typeof height === 'number' && height > 0
          ? {
              widthIn: width,
              heightIn: height,
              marginIn: margin,
              galleryWrapIn: galleryWrap,
              galleryWrapMirrorEdges: mirrorEdges,
            }
          : undefined,
      );
      if (nextValue) {
        draftChangeHandler(nextValue);
        return;
      }

      draftChangeHandler(undefined);
    },
    [draftChangeHandler],
  );

  const handleWidthChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setWidthInputValue(value);
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setWidthIn(undefined);
      syncDraftSize(undefined, heightIn);
      return;
    }
    const inchesValueRaw = convertUnitToInches(parsed, measurementUnit);
    const nextWidth = clampTwoDecimals(inchesValueRaw);
    if (typeof nextWidth !== 'number' || Number.isNaN(nextWidth)) {
      setWidthIn(undefined);
      return;
    }

    setWidthIn(nextWidth);

    const ratio = resolveAspectRatio();
    if (ratio && ratio > 0) {
      const nextHeightRaw = nextWidth / ratio;
      const nextHeight = clampTwoDecimals(nextHeightRaw);
      const safeHeight = typeof nextHeight === 'number' && nextHeight > 0 ? nextHeight : 0.01;
      setHeightIn(safeHeight);
      setAspectRatio(nextWidth / safeHeight);
      syncDraftSize(
        nextWidth,
        safeHeight,
        isMarginEnabled ? marginIn : undefined,
        isGalleryWrapEnabled ? galleryWrapIn : undefined,
        isGalleryWrapEnabled ? galleryWrapMirrorEdges : undefined,
      );
      return;
    }

    syncDraftSize(
      nextWidth,
      heightIn,
      isMarginEnabled ? marginIn : undefined,
      isGalleryWrapEnabled ? galleryWrapIn : undefined,
      isGalleryWrapEnabled ? galleryWrapMirrorEdges : undefined,
    );
  }, [galleryWrapIn, galleryWrapMirrorEdges, heightIn, isGalleryWrapEnabled, isMarginEnabled, marginIn, measurementUnit, resolveAspectRatio, syncDraftSize]);

  const handleHeightChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setHeightInputValue(value);
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setHeightIn(undefined);
      return;
    }
    const inchesValueRaw = convertUnitToInches(parsed, measurementUnit);
    const nextHeight = clampTwoDecimals(inchesValueRaw);
    if (typeof nextHeight !== 'number' || Number.isNaN(nextHeight)) {
      setHeightIn(undefined);
      return;
    }

    setHeightIn(nextHeight);

    const ratio = resolveAspectRatio();
    if (ratio && ratio > 0) {
      const nextWidthRaw = nextHeight * ratio;
      const nextWidth = clampTwoDecimals(nextWidthRaw);
      const safeWidth = typeof nextWidth === 'number' && nextWidth > 0 ? nextWidth : 0.01;
      setWidthIn(safeWidth);
      setAspectRatio(safeWidth / nextHeight);
      syncDraftSize(
        safeWidth,
        nextHeight,
        isMarginEnabled ? marginIn : undefined,
        isGalleryWrapEnabled ? galleryWrapIn : undefined,
        isGalleryWrapEnabled ? galleryWrapMirrorEdges : undefined,
      );
      return;
    }

    syncDraftSize(
      widthIn,
      nextHeight,
      isMarginEnabled ? marginIn : undefined,
      isGalleryWrapEnabled ? galleryWrapIn : undefined,
      isGalleryWrapEnabled ? galleryWrapMirrorEdges : undefined,
    );
  }, [galleryWrapIn, galleryWrapMirrorEdges, isGalleryWrapEnabled, isMarginEnabled, marginIn, measurementUnit, resolveAspectRatio, syncDraftSize, widthIn]);

  const commitSave = useCallback(async () => {
    if (!item || !widthIn || !heightIn) {
      setError(t('setPrintSize.validationError', 'Enter valid width and height.'));
      analytics.track('button: ui.print_size_save_failed', { reason: 'invalid_dimensions' });
      return;
    }
    if (sizeLimitValidation) {
      analytics.track('button: ui.print_size_save_failed', {
        reason: 'paper_size_limit',
        maxShortSideIn: sizeLimitValidation.maxShortSideIn,
        maxLongSideIn: sizeLimitValidation.maxLongSideIn,
      });
      return;
    }
    if (isMarginEnabled && (!marginIn || marginIn <= 0)) {
      setError(t('setPrintSize.marginValidation', 'Enter a valid margin greater than zero.'));
      analytics.track('button: ui.print_size_save_failed', { reason: 'invalid_margin' });
      return;
    }
    if (isGalleryWrapEnabled && (!galleryWrapIn || galleryWrapIn <= 0)) {
      setError(t('setPrintSize.galleryWrapValidation', 'Enter a valid gallery wrap greater than zero.'));
      analytics.track('button: ui.print_size_save_failed', { reason: 'invalid_gallery_wrap' });
      return;
    }
    if (isMarginEnabled && isGalleryWrapEnabled) {
      setError(t('setPrintSize.edgeTreatmentValidation', 'Choose either margin or gallery wrap.'));
      analytics.track('button: ui.print_size_save_failed', { reason: 'edge_treatment_conflict' });
      return;
    }

    const targetId = item.id;
    if (!targetId) {
      setError(t('setPrintSize.missingFile', 'File data is not available.'));
      analytics.track('button: ui.print_size_save_failed', { reason: 'missing_file' });
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      const payload: PrintSizeValue = { widthIn, heightIn };
      if (isMarginEnabled) {
        payload.marginIn = marginIn ?? DEFAULT_MARGIN_IN;
      } else if (isGalleryWrapEnabled) {
        payload.galleryWrapIn = galleryWrapIn ?? DEFAULT_GALLERY_WRAP_IN;
        payload.galleryWrapMirrorEdges = galleryWrapMirrorEdges;
      }

      await Promise.resolve(onSave(payload));
      analytics.track('button: ui.print_size_save', {
        itemId: targetId,
        widthIn,
        heightIn,
        marginIn: isMarginEnabled ? (marginIn ?? DEFAULT_MARGIN_IN) : null,
        galleryWrapIn: isGalleryWrapEnabled ? (galleryWrapIn ?? DEFAULT_GALLERY_WRAP_IN) : null,
        galleryWrapMirrorEdges: isGalleryWrapEnabled ? galleryWrapMirrorEdges : false,
        unit: measurementUnit,
      });

      if (isDraftControlled) {
        emitDraftChange(undefined);
        onDraftReset?.();
        return;
      }

      didSaveRef.current = true;
      initialSizeRef.current = {
        key: targetId,
        widthIn,
        heightIn,
        marginIn: isMarginEnabled ? (marginIn ?? DEFAULT_MARGIN_IN) : undefined,
        galleryWrapIn: isGalleryWrapEnabled ? (galleryWrapIn ?? DEFAULT_GALLERY_WRAP_IN) : undefined,
        galleryWrapMirrorEdges: isGalleryWrapEnabled ? galleryWrapMirrorEdges : false,
      };
      emitDraftChange(undefined);
      onDraftReset?.();
      onClose();
    } finally {
      setIsSaving(false);
    }
  }, [emitDraftChange, galleryWrapIn, galleryWrapMirrorEdges, heightIn, isDraftControlled, isGalleryWrapEnabled, isMarginEnabled, item, marginIn, measurementUnit, onClose, onSave, onDraftReset, sizeLimitValidation, t, widthIn]);

  const commitClose = useCallback(() => {
    if (!didSaveRef.current) {
      revertToInitialSize();
      onDraftReset?.();
      emitDraftChange(undefined);
    }
    analytics.track('button: ui.print_size_cancel', { itemId: item?.id ?? null });
    onClose();
  }, [emitDraftChange, item?.id, onClose, onDraftReset, revertToInitialSize]);

  const title = t('setPrintSize.title', 'Set print size');
  const subtitle = t('setPrintSize.subtitle', 'Adjust the printable area with real-time preview.');
  const saveButtonLabel = saveLabel ?? t('setPrintSize.confirmCta', 'Confirm size');

  const { dpiX: currentDpiX, dpiY: currentDpiY } = useMemo(
    () =>
      calculateCurrentDpi({
        pixelWidth: metadata?.width ?? null,
        pixelHeight: metadata?.height ?? null,
        widthIn: widthIn ?? null,
        heightIn: heightIn ?? null,
        marginIn: isMarginEnabled ? marginIn ?? null : null,
      }),
    [metadata?.height, metadata?.width, heightIn, widthIn, isMarginEnabled, marginIn],
  );

  const currentPrintQualityWarning = useMemo<PrintQualityWarning | null>(() => {
    if (typeof currentDpiX !== 'number' || typeof currentDpiY !== 'number') {
      return null;
    }
    const ppi = Math.min(currentDpiX, currentDpiY);
    if (ppi >= PRINT_QUALITY_MIN_PPI) {
      return null;
    }
    return {
      type: 'LOW_PPI',
      ppi,
      threshold: PRINT_QUALITY_MIN_PPI,
    };
  }, [currentDpiX, currentDpiY]);
  const isCurrentWarningAcknowledged = useMemo(() => {
    if (!currentPrintQualityWarning || item?.printQualityWarning?.type !== 'LOW_PPI') {
      return false;
    }
    return item.printQualityWarning.acknowledged === true;
  }, [currentPrintQualityWarning, item?.printQualityWarning]);

  const syncPrintQualityWarningForSave = useCallback(() => {
    const itemId = item?.id;
    if (!itemId) {
      return;
    }
    const existing = item.printQualityWarning;

    if (!currentPrintQualityWarning) {
      if (existing != null) {
        dispatch(updateOrderItem({ orderItemId: itemId, data: { printQualityWarning: null } }));
      }
      setPendingQualityAction(null);
      return;
    }

    if (existing?.type === 'LOW_PPI'
      && existing.ppi === currentPrintQualityWarning.ppi
      && existing.threshold === currentPrintQualityWarning.threshold) {
      return;
    }

    const nextWarning = existing?.type === 'LOW_PPI' && existing.acknowledged === true
      ? {
        ...currentPrintQualityWarning,
        acknowledged: true,
        reason: existing.reason ?? LOW_PPI_ACKNOWLEDGEMENT_REASON,
      }
      : currentPrintQualityWarning;

    dispatch(updateOrderItem({
      orderItemId: itemId,
      data: { printQualityWarning: nextWarning },
    }));
  }, [currentPrintQualityWarning, dispatch, item?.id, item?.printQualityWarning]);

  const requestPrintQualityConfirmation = useCallback(() => {
    if (!currentPrintQualityWarning || isCurrentWarningAcknowledged) {
      return false;
    }

    setPendingQualityAction('save');
    onRequestPrintQualityHelp?.(currentPrintQualityWarning);
    analytics.track('system: ui.print_size_low_ppi_warning', {
      itemId: item?.id ?? null,
      ppi: currentPrintQualityWarning.ppi,
      threshold: currentPrintQualityWarning.threshold,
      action: 'save',
    });
    return true;
  }, [currentPrintQualityWarning, isCurrentWarningAcknowledged, item?.id, onRequestPrintQualityHelp]);

  const handleSave = useCallback(async () => {
    if (sizeLimitValidation) {
      analytics.track('button: ui.print_size_save_failed', {
        reason: 'paper_size_limit',
        maxShortSideIn: sizeLimitValidation.maxShortSideIn,
        maxLongSideIn: sizeLimitValidation.maxLongSideIn,
      });
      return;
    }
    if (requestPrintQualityConfirmation()) {
      return;
    }
    syncPrintQualityWarningForSave();
    await commitSave();
  }, [commitSave, requestPrintQualityConfirmation, sizeLimitValidation, syncPrintQualityWarningForSave]);

  const handleCancel = useCallback(() => {
    commitClose();
  }, [commitClose]);

  const handleBack = useCallback(() => {
    if (onBack) {
      onBack();
      return;
    }
    commitClose();
  }, [commitClose, onBack]);

  const handleAcceptPrintQualityWarning = useCallback(() => {
    const itemId = item?.id;
    if (!itemId || !currentPrintQualityWarning || !pendingQualityAction) {
      return;
    }

    dispatch(updateOrderItem({
      orderItemId: itemId,
      data: {
        printQualityWarning: {
          ...currentPrintQualityWarning,
          acknowledged: true,
          reason: LOW_PPI_ACKNOWLEDGEMENT_REASON,
        },
      },
    }));

    const action = pendingQualityAction;
    setPendingQualityAction(null);
    analytics.track('button: ui.print_size_low_ppi_accept', {
      itemId,
      ppi: currentPrintQualityWarning.ppi,
      threshold: currentPrintQualityWarning.threshold,
      action,
    });

    if (action === 'save') {
      void commitSave();
    }
  }, [commitSave, currentPrintQualityWarning, dispatch, item?.id, pendingQualityAction]);

  const displayWidthValue = useMemo(() => {
    if (typeof widthIn !== 'number' || widthIn <= 0) {
      return '';
    }
    const converted = convertInchesToUnit(widthIn, measurementUnit);
    return converted.toFixed(precision);
  }, [measurementUnit, precision, widthIn]);

  const displayHeightValue = useMemo(() => {
    if (typeof heightIn !== 'number' || heightIn <= 0) {
      return '';
    }
    const converted = convertInchesToUnit(heightIn, measurementUnit);
    return converted.toFixed(precision);
  }, [heightIn, measurementUnit, precision]);

  const displayMarginValue = useMemo(() => {
    if (typeof marginIn !== 'number' || marginIn <= 0) {
      return '';
    }
    const converted = convertInchesToUnit(marginIn, measurementUnit);
    return converted.toFixed(precision);
  }, [marginIn, measurementUnit, precision]);

  const displayGalleryWrapValue = useMemo(() => {
    if (typeof galleryWrapIn !== 'number' || galleryWrapIn <= 0) {
      return '';
    }
    const converted = convertInchesToUnit(galleryWrapIn, measurementUnit);
    return converted.toFixed(precision);
  }, [galleryWrapIn, measurementUnit, precision]);

  const fallbackAspectRatio = useMemo(() => {
    if (
      typeof totalCanvasDimensions.widthIn === 'number'
      && typeof totalCanvasDimensions.heightIn === 'number'
      && totalCanvasDimensions.heightIn > 0
    ) {
      return Math.max(totalCanvasDimensions.widthIn / totalCanvasDimensions.heightIn, 0.1);
    }
    if (
      typeof metadata?.width === 'number'
      && typeof metadata?.height === 'number'
      && metadata.height > 0
    ) {
      return Math.max(metadata.width / metadata.height, 0.1);
    }
    return 1;
  }, [metadata?.height, metadata?.width, totalCanvasDimensions.heightIn, totalCanvasDimensions.widthIn]);

  useEffect(() => {
    if (!isWidthInputFocused) {
      setWidthInputValue(displayWidthValue);
    }
  }, [displayWidthValue, isWidthInputFocused]);

  useEffect(() => {
    if (!isHeightInputFocused) {
      setHeightInputValue(displayHeightValue);
    }
  }, [displayHeightValue, isHeightInputFocused]);

  useEffect(() => {
    if (!isMarginInputFocused) {
      setMarginInputValue(displayMarginValue);
    }
  }, [displayMarginValue, isMarginInputFocused]);

  useEffect(() => {
    if (!isGalleryWrapInputFocused) {
      setGalleryWrapInputValue(displayGalleryWrapValue);
    }
  }, [displayGalleryWrapValue, isGalleryWrapInputFocused]);

  const handleWidthFocus = useCallback(() => {
    setIsWidthInputFocused(true);
  }, []);

  const handleWidthBlur = useCallback(() => {
    setIsWidthInputFocused(false);
    setWidthInputValue(displayWidthValue);
    analytics.track('input: ui.print_size_width_set', {
      widthIn: widthIn ?? null,
      unit: measurementUnit,
    });
  }, [displayWidthValue, measurementUnit, widthIn]);

  const handleHeightFocus = useCallback(() => {
    setIsHeightInputFocused(true);
  }, []);

  const handleHeightBlur = useCallback(() => {
    setIsHeightInputFocused(false);
    setHeightInputValue(displayHeightValue);
    analytics.track('input: ui.print_size_height_set', {
      heightIn: heightIn ?? null,
      unit: measurementUnit,
    });
  }, [displayHeightValue, heightIn, measurementUnit]);

  const handleMarginFocus = useCallback(() => {
    setIsMarginInputFocused(true);
  }, []);

  const handleMarginBlur = useCallback(() => {
    setIsMarginInputFocused(false);
    setMarginInputValue(displayMarginValue);
    analytics.track('input: ui.print_size_margin_set', {
      marginIn: marginIn ?? null,
      unit: measurementUnit,
      enabled: isMarginEnabled,
    });
  }, [displayMarginValue, isMarginEnabled, marginIn, measurementUnit]);

  const handleGalleryWrapFocus = useCallback(() => {
    setIsGalleryWrapInputFocused(true);
  }, []);

  const handleGalleryWrapBlur = useCallback(() => {
    setIsGalleryWrapInputFocused(false);
    setGalleryWrapInputValue(displayGalleryWrapValue);
    analytics.track('input: ui.print_size_gallery_wrap_set', {
      galleryWrapIn: galleryWrapIn ?? null,
      unit: measurementUnit,
      enabled: isGalleryWrapEnabled,
    });
  }, [displayGalleryWrapValue, galleryWrapIn, isGalleryWrapEnabled, measurementUnit]);

  const handleMarginToggle = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const checked = event.target.checked;
      setIsMarginEnabled(checked);
      analytics.track('checkbox: ui.print_size_margin_toggle', { enabled: checked });
      if (checked) {
        const nextMargin = typeof marginIn === 'number' && marginIn > 0 ? marginIn : DEFAULT_MARGIN_IN;
        setMarginIn(nextMargin);
        setMarginInputValue(convertInchesToUnit(nextMargin, measurementUnit).toFixed(precision));
        setIsGalleryWrapEnabled(false);
        setGalleryWrapIn(undefined);
        setGalleryWrapInputValue('');
        setGalleryWrapMirrorEdges(false);
        syncDraftSize(widthIn, heightIn, nextMargin, undefined);
        return;
      }
      setMarginIn(undefined);
      setMarginInputValue('');
      syncDraftSize(widthIn, heightIn, undefined, isGalleryWrapEnabled ? galleryWrapIn : undefined, isGalleryWrapEnabled ? galleryWrapMirrorEdges : undefined);
    },
    [galleryWrapIn, galleryWrapMirrorEdges, heightIn, isGalleryWrapEnabled, marginIn, measurementUnit, precision, syncDraftSize, widthIn],
  );

  const handleMarginOptionClick = useCallback(
    (event: React.MouseEvent<HTMLInputElement>) => {
      if (!isMarginEnabled) {
        return;
      }
      event.preventDefault();
      setIsMarginEnabled(false);
      setMarginIn(undefined);
      setMarginInputValue('');
      syncDraftSize(widthIn, heightIn, undefined, undefined);
      analytics.track('radio: ui.print_size_margin_toggle', { enabled: false });
    },
    [heightIn, isMarginEnabled, syncDraftSize, widthIn],
  );

  const handleMarginChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setMarginInputValue(value);
      const parsed = Number(value);
      if (!Number.isFinite(parsed) || parsed <= 0) {
        setMarginIn(undefined);
        syncDraftSize(widthIn, heightIn, undefined, undefined);
        return;
      }
      const inchesValueRaw = convertUnitToInches(parsed, measurementUnit);
      const nextMargin = clampTwoDecimals(inchesValueRaw);
      if (typeof nextMargin !== 'number' || Number.isNaN(nextMargin)) {
        setMarginIn(undefined);
        syncDraftSize(widthIn, heightIn, undefined, undefined);
        return;
      }
      setMarginIn(nextMargin);
      setIsGalleryWrapEnabled(false);
      setGalleryWrapIn(undefined);
      setGalleryWrapInputValue('');
      setGalleryWrapMirrorEdges(false);
      syncDraftSize(widthIn, heightIn, nextMargin, undefined);
    },
    [heightIn, measurementUnit, syncDraftSize, widthIn],
  );

  const handleGalleryWrapToggle = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const checked = event.target.checked;
      setIsGalleryWrapEnabled(checked);
      analytics.track('radio: ui.print_size_gallery_wrap_toggle', { enabled: checked });
      if (checked) {
        const nextGalleryWrap = typeof galleryWrapIn === 'number' && galleryWrapIn > 0
          ? galleryWrapIn
          : DEFAULT_GALLERY_WRAP_IN;
        setGalleryWrapIn(nextGalleryWrap);
        setGalleryWrapInputValue(convertInchesToUnit(nextGalleryWrap, measurementUnit).toFixed(precision));
        setIsMarginEnabled(false);
        setMarginIn(undefined);
        setMarginInputValue('');
        syncDraftSize(widthIn, heightIn, undefined, nextGalleryWrap, galleryWrapMirrorEdges);
        return;
      }
      setGalleryWrapIn(undefined);
      setGalleryWrapInputValue('');
      setGalleryWrapMirrorEdges(false);
      syncDraftSize(widthIn, heightIn, isMarginEnabled ? marginIn : undefined, undefined);
    },
    [galleryWrapIn, galleryWrapMirrorEdges, heightIn, isMarginEnabled, marginIn, measurementUnit, precision, syncDraftSize, widthIn],
  );

  const handleGalleryWrapOptionClick = useCallback(
    (event: React.MouseEvent<HTMLInputElement>) => {
      if (!isGalleryWrapEnabled) {
        return;
      }
      event.preventDefault();
      setIsGalleryWrapEnabled(false);
      setGalleryWrapIn(undefined);
      setGalleryWrapInputValue('');
      setGalleryWrapMirrorEdges(false);
      syncDraftSize(widthIn, heightIn, undefined, undefined);
      analytics.track('radio: ui.print_size_gallery_wrap_toggle', { enabled: false });
    },
    [heightIn, isGalleryWrapEnabled, syncDraftSize, widthIn],
  );

  const handleGalleryWrapChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setGalleryWrapInputValue(value);
      const parsed = Number(value);
      if (!Number.isFinite(parsed) || parsed <= 0) {
        setGalleryWrapIn(undefined);
        syncDraftSize(widthIn, heightIn, undefined, undefined);
        return;
      }
      const inchesValueRaw = convertUnitToInches(parsed, measurementUnit);
      const nextGalleryWrap = clampTwoDecimals(inchesValueRaw);
      if (typeof nextGalleryWrap !== 'number' || Number.isNaN(nextGalleryWrap)) {
        setGalleryWrapIn(undefined);
        syncDraftSize(widthIn, heightIn, undefined, undefined);
        return;
      }
      setGalleryWrapIn(nextGalleryWrap);
      setIsMarginEnabled(false);
      setMarginIn(undefined);
      setMarginInputValue('');
      syncDraftSize(widthIn, heightIn, undefined, nextGalleryWrap, galleryWrapMirrorEdges);
    },
    [galleryWrapMirrorEdges, heightIn, measurementUnit, syncDraftSize, widthIn],
  );

  const handleGalleryWrapMirrorEdgesChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setGalleryWrapMirrorEdges(checked);
    syncDraftSize(widthIn, heightIn, undefined, isGalleryWrapEnabled ? galleryWrapIn : undefined, checked);
    analytics.track('checkbox: ui.print_size_gallery_wrap_mirror_edges_toggle', { enabled: checked });
  }, [galleryWrapIn, heightIn, isGalleryWrapEnabled, syncDraftSize, widthIn]);

  const unitOptions: readonly MeasurementUnit[] = MEASUREMENT_UNITS;
  const isSizeLimitInvalid = Boolean(sizeLimitValidation);
  const isSaveDisabled = isLoading || isSaving || isSizeLimitInvalid || saveDisabled;

  if (!item) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 p-6 text-center">
        <p className="text-lg font-semibold text-muted-foreground">
          {t('setPrintSize.noItem', 'Select an image to adjust its print size.')}
        </p>
        <Button variant="outline" onClick={handleBack}>
          {t('common.back', 'Back')}
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto flex h-full w-full max-w-5xl flex-col overflow-hidden bg-background p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border pb-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={handleBack} aria-label={t('common.back', 'Back')}>
            <ArrowLeft className="h-5 w-5" aria-hidden />
          </Button>
          <div>
            <p className="text-lg font-semibold text-foreground sm:text-xl">{title}</p>
            <p className="text-sm text-muted-foreground">{subtitle}</p>
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-2">
          <Button variant="outline" onClick={handleCancel}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button onClick={handleSave} disabled={isSaveDisabled}>
            {isSaving || isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {saveButtonLabel}
          </Button>
        </div>
      </div>
      <div className="flex-1 min-h-0 py-4">
        <div className="grid h-full gap-4 overflow-auto lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] lg:gap-6">
          <section className="flex min-h-[360px] flex-col gap-4 rounded-2xl border border-border bg-muted/10 p-4 sm:p-6 lg:h-full">
            <header className="flex flex-col">
              <span className="text-sm font-medium text-muted-foreground">
                {t('setPrintSize.previewLabel', 'Preview')}
              </span>
              <span className="text-xs font-medium text-muted-foreground sm:text-sm">
                {t('setPrintSize.artworkSizeWithUnit', 'Artwork: {{width}} {{unit}} × {{height}} {{unit}}', {
                  width: formatDimension(widthIn),
                  height: formatDimension(heightIn),
                  unit: unitLabel,
                })}
              </span>
            </header>
            <div className="relative flex flex-1 items-center justify-center overflow-hidden rounded-2xl border border-border bg-background">
              {imageSrc ? (
                <div className="relative h-full w-full" ref={previewViewportRef}>
                  <div className="flex h-full w-full items-center justify-center bg-background/80 p-4">
                    {previewMetrics ? (
                      <div
                        ref={previewCanvasRef}
                        className="relative rounded-md border border-dashed border-muted-foreground/70 bg-white shadow-sm"
                        style={{
                          width: previewMetrics.canvasWidth,
                          height: previewMetrics.canvasHeight,
                        }}
                      >
                        {isMirroredGalleryWrapPreview ? (
                          <canvas
                            ref={previewRasterRef}
                            className="absolute inset-0 z-0 h-full w-full rounded-md"
                            aria-hidden="true"
                          />
                        ) : (
                          <div className="absolute inset-0 z-0 flex items-center justify-center">
                            <div
                              style={{
                                width: previewMetrics.artworkWidth,
                                height: previewMetrics.artworkHeight,
                              }}
                            >
                              <img
                                src={imageSrc}
                                alt={title}
                                className="h-full w-full rounded-md object-contain"
                              />
                            </div>
                          </div>
                        )}
                        {(previewMetrics.isMarginActive || previewMetrics.isGalleryWrapActive) && !isMirroredGalleryWrapPreview ? (
                          <div
                            className="pointer-events-none absolute inset-0 z-10"
                            data-testid="edge-treatment-preview-overlay"
                          >
                            <div
                              className="absolute inset-x-0 top-0 border-b border-slate-500/20 bg-slate-200/55"
                              style={{
                                height: previewMetrics.marginPx,
                                backgroundImage: MARGIN_PREVIEW_PATTERN,
                              }}
                            />
                            <div
                              className="absolute inset-x-0 bottom-0 border-t border-slate-500/20 bg-slate-200/55"
                              style={{
                                height: previewMetrics.marginPx,
                                backgroundImage: MARGIN_PREVIEW_PATTERN,
                              }}
                            />
                            <div
                              className="absolute left-0 border-r border-slate-500/20 bg-slate-200/55"
                              style={{
                                top: 0,
                                bottom: 0,
                                width: previewMetrics.marginPx,
                                backgroundImage: MARGIN_PREVIEW_PATTERN,
                              }}
                            />
                            <div
                              className="absolute right-0 border-l border-slate-500/20 bg-slate-200/55"
                              style={{
                                top: 0,
                                bottom: 0,
                                width: previewMetrics.marginPx,
                                backgroundImage: MARGIN_PREVIEW_PATTERN,
                              }}
                            />
                          </div>
                        ) : null}
                        {previewMetrics.isGalleryWrapActive ? (
                          <div
                            className="pointer-events-none absolute z-20 rounded-sm border border-slate-500/35"
                            style={{
                              left: previewMetrics.offsetX,
                              top: previewMetrics.offsetY,
                              width: previewMetrics.artworkWidth,
                              height: previewMetrics.artworkHeight,
                            }}
                            data-testid="gallery-wrap-artwork-bounds"
                          />
                        ) : null}
                      </div>
                    ) : (
                      <div className="flex max-h-full w-full items-center justify-center">
                        <div
                          ref={previewCanvasRef}
                          className="relative flex h-auto w-full max-w-[420px] items-center justify-center rounded-md border border-dashed border-muted-foreground/70 bg-white shadow-sm"
                          style={{ aspectRatio: fallbackAspectRatio }}
                        >
                          <img src={imageSrc} alt={title} className="h-full w-full rounded-md object-contain" />
                        </div>
                      </div>
                    )}
                  </div>
                  {dimensionBounds ? (
                    <>
                      {previewMetrics?.isGalleryWrapActive ? null : (
                        <EngineeringDimensionCallouts
                          width={typeof totalCanvasDimensions.widthIn === 'number' ? convertInchesToUnit(totalCanvasDimensions.widthIn, measurementUnit) : undefined}
                          height={typeof totalCanvasDimensions.heightIn === 'number' ? convertInchesToUnit(totalCanvasDimensions.heightIn, measurementUnit) : undefined}
                          unit={unitLabel}
                          precision={precision}
                          bounds={dimensionBounds}
                        />
                      )}
                      {previewMetrics?.isGalleryWrapActive ? (
                        <EngineeringDimensionCallouts
                          width={typeof widthIn === 'number' ? convertInchesToUnit(widthIn, measurementUnit) : undefined}
                          height={typeof heightIn === 'number' ? convertInchesToUnit(heightIn, measurementUnit) : undefined}
                          unit={unitLabel}
                          precision={precision}
                          horizontalDimensionYPx={Math.max(dimensionBounds.top - 12, 0)}
                          verticalDimensionXPx={Math.min(dimensionBounds.right + 12, dimensionBounds.viewportWidth)}
                          bounds={{
                            left: Math.min(dimensionBounds.left + previewMetrics.offsetX, dimensionBounds.right),
                            right: Math.min(dimensionBounds.left + previewMetrics.offsetX + previewMetrics.artworkWidth, dimensionBounds.right),
                            top: Math.min(dimensionBounds.top + previewMetrics.offsetY, dimensionBounds.bottom),
                            bottom: Math.min(dimensionBounds.top + previewMetrics.offsetY + previewMetrics.artworkHeight, dimensionBounds.bottom),
                            viewportWidth: dimensionBounds.viewportWidth,
                            viewportHeight: dimensionBounds.viewportHeight,
                          }}
                        />
                      ) : null}
                      {(marginLabel || galleryWrapLabel) && previewMetrics ? (
                        <>
                          <EngineeringDimensionCallouts
                            horizontalLabel={marginLabel ?? galleryWrapLabel ?? undefined}
                            horizontalPlacement="bottom"
                            unit={unitLabel}
                            precision={precision}
                            bounds={{
                              left: dimensionBounds.left,
                              right: Math.min(dimensionBounds.left + previewMetrics.marginPx, dimensionBounds.right),
                              top: Math.max(dimensionBounds.bottom - previewMetrics.marginPx, dimensionBounds.top),
                              bottom: dimensionBounds.bottom,
                              viewportWidth: dimensionBounds.viewportWidth,
                              viewportHeight: dimensionBounds.viewportHeight,
                            }}
                          />
                          <EngineeringDimensionCallouts
                            verticalLabel={marginLabel ?? galleryWrapLabel ?? undefined}
                            verticalPlacement="left"
                            unit={unitLabel}
                            precision={precision}
                            bounds={{
                              left: dimensionBounds.left,
                              right: dimensionBounds.right,
                              top: dimensionBounds.top,
                              bottom: Math.min(dimensionBounds.top + previewMetrics.marginPx, dimensionBounds.bottom),
                              viewportWidth: dimensionBounds.viewportWidth,
                              viewportHeight: dimensionBounds.viewportHeight,
                            }}
                          />
                        </>
                      ) : null}
                    </>
                  ) : null}
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2 text-sm text-muted-foreground">
                  {isLoading ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
                      <span>{t('common.loading', 'Loading...')}</span>
                    </>
                  ) : (
                    <span>{t('setPrintSize.noPreview', 'Preview unavailable')}</span>
                  )}
                </div>
              )}
            </div>
          </section>
          <section className="flex flex-col gap-4">
            <div className="rounded-2xl border border-border bg-card p-4 shadow-sm sm:p-6">
              <div className="mb-4 flex items-center gap-2">
                <Info className="h-4 w-4 text-muted-foreground" aria-hidden />
                <p className="text-sm font-semibold text-foreground sm:text-base">
                  {t('setPrintSize.adjustSize', 'Adjust print size')}
                </p>
              </div>
              <div className="mb-4 flex items-center gap-2 text-xs font-medium text-muted-foreground sm:text-sm">
                <span>{t('setPrintSize.unitsLabel', 'Units')}</span>
                <div className="inline-flex rounded-full border border-border/70 bg-muted/20 p-1">
                  {unitOptions.map(unit => {
                    const isActive = unit === measurementUnit;
                    const label = translateMeasurementUnitLabel(t, unit);
                    return (
                      <button
                        key={unit}
                        type="button"
                        className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                          isActive
                            ? 'bg-primary text-primary-foreground shadow'
                            : 'text-muted-foreground hover:bg-muted/60'
                        }`}
                        onClick={() => {
                          dispatch(setMeasurementUnit(unit));
                          analytics.track('button: ui.print_size_unit_change', { unit });
                        }}
                        aria-pressed={isActive}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="widthIn">
                    {t('setPrintSize.widthLabelUnit', 'Width ({{unit}})', { unit: unitLabel })}
                  </Label>
                  <div className="relative flex items-center gap-1">
                    <Input
                      id="widthIn"
                      type="number"
                      step={inputStep}
                      min={inputMin}
                      value={isWidthInputFocused ? widthInputValue : displayWidthValue}
                      onChange={handleWidthChange}
                      onFocus={handleWidthFocus}
                      onBlur={handleWidthBlur}
                      disabled={isLoading}
                      inputMode="decimal"
                      className="min-w-0"
                    />
                    {galleryWrapBillableIncreaseLabel ? (
                      <span
                        className="pointer-events-none shrink-0 -rotate-2 select-none font-serif text-sm italic leading-none text-red-600"
                        aria-hidden="true"
                        title={t('setPrintSize.galleryWrapBillableIncreaseHint', 'Gallery wrap increases the billable print size.')}
                      >
                        {galleryWrapBillableIncreaseLabel}
                      </span>
                    ) : null}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="heightIn">
                    {t('setPrintSize.heightLabelUnit', 'Height ({{unit}})', { unit: unitLabel })}
                  </Label>
                  <div className="relative flex items-center gap-1">
                    <Input
                      id="heightIn"
                      type="number"
                      step={inputStep}
                      min={inputMin}
                      value={isHeightInputFocused ? heightInputValue : displayHeightValue}
                      onChange={handleHeightChange}
                      onFocus={handleHeightFocus}
                      onBlur={handleHeightBlur}
                      disabled={isLoading}
                      inputMode="decimal"
                      className="min-w-0"
                    />
                    {galleryWrapBillableIncreaseLabel ? (
                      <span
                        className="pointer-events-none shrink-0 rotate-1 select-none font-serif text-sm italic leading-none text-red-600"
                        aria-hidden="true"
                        title={t('setPrintSize.galleryWrapBillableIncreaseHint', 'Gallery wrap increases the billable print size.')}
                      >
                        {galleryWrapBillableIncreaseLabel}
                      </span>
                    ) : null}
                  </div>
                </div>
                {sizeLimitValidation ? (
                  <p className="text-sm text-destructive sm:col-span-2">
                    {sizeLimitValidation.message}
                  </p>
                ) : null}
                <div className="space-y-2 sm:col-span-2">
                  <div className="flex flex-wrap gap-4">
                    <label className="inline-flex items-center gap-2 text-sm font-semibold text-foreground">
                      <input
                        id="marginToggle"
                        type="radio"
                        name="edgeTreatment"
                        className="h-4 w-4 border border-border text-primary focus:ring-primary"
                        checked={isMarginEnabled}
                        onChange={handleMarginToggle}
                        onClick={handleMarginOptionClick}
                        disabled={isLoading}
                      />
                      <span>{t('setPrintSize.marginToggleLabel', 'I have margin')}</span>
                    </label>
                    <label className="inline-flex items-center gap-2 text-sm font-semibold text-foreground">
                      <input
                        id="galleryWrapToggle"
                        type="radio"
                        name="edgeTreatment"
                        className="h-4 w-4 border border-border text-primary focus:ring-primary"
                        checked={isGalleryWrapEnabled}
                        onChange={handleGalleryWrapToggle}
                        onClick={handleGalleryWrapOptionClick}
                        disabled={isLoading}
                      />
                      <span>{t('setPrintSize.galleryWrapToggleLabel', 'Gallery wrap')}</span>
                    </label>
                  </div>
                  {isMarginEnabled ? (
                    <div className="max-w-xs space-y-2">
                      <Label htmlFor="marginIn">
                        {t('setPrintSize.marginLabelUnit', 'Margin ({{unit}})', { unit: unitLabel })}
                      </Label>
                      <Input
                        id="marginIn"
                        type="number"
                        step={inputStep}
                        min={inputMin}
                        value={isMarginInputFocused ? marginInputValue : displayMarginValue}
                        onChange={handleMarginChange}
                        onFocus={handleMarginFocus}
                        onBlur={handleMarginBlur}
                        disabled={isLoading}
                        inputMode="decimal"
                      />
                    </div>
                  ) : null}
                  {isGalleryWrapEnabled ? (
                    <div className="max-w-xs space-y-3">
                      <div className="space-y-2">
                        <Label htmlFor="galleryWrapIn">
                          {t('setPrintSize.galleryWrapLabelUnit', 'Gallery wrap ({{unit}})', { unit: unitLabel })}
                        </Label>
                        <Input
                          id="galleryWrapIn"
                          type="number"
                          step={inputStep}
                          min={inputMin}
                          value={isGalleryWrapInputFocused ? galleryWrapInputValue : displayGalleryWrapValue}
                          onChange={handleGalleryWrapChange}
                          onFocus={handleGalleryWrapFocus}
                          onBlur={handleGalleryWrapBlur}
                          disabled={isLoading}
                          inputMode="decimal"
                        />
                      </div>
                      <label className="inline-flex items-center gap-2 text-sm text-foreground">
                        <input
                          id="galleryWrapMirrorEdges"
                          type="checkbox"
                          className="h-4 w-4 rounded border border-border text-primary focus:ring-primary"
                          checked={galleryWrapMirrorEdges}
                          onChange={handleGalleryWrapMirrorEdgesChange}
                          disabled={isLoading}
                        />
                        <span>{t('setPrintSize.galleryWrapMirrorEdgesLabel', 'Mirror edges')}</span>
                      </label>
                    </div>
                  ) : null}
                </div>
              </div>
              {error ? <p className="mt-3 text-sm text-destructive">{error}</p> : null}
              {pendingQualityAction && currentPrintQualityWarning ? (
                <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-900">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden />
                    <div className="min-w-0 flex-1 space-y-2">
                      <p className="text-sm font-semibold">
                        {t('setPrintSize.lowPpiTitle', 'Print quality may be low')}
                      </p>
                      <p className="text-xs leading-5">
                        {t(
                          'setPrintSize.lowPpiMessage',
                          'This image is {{ppi}} PPI at the selected print size. For sharp printed results, {{threshold}} PPI or higher is recommended.',
                          {
                            ppi: Math.round(currentPrintQualityWarning.ppi),
                            threshold: currentPrintQualityWarning.threshold,
                          },
                        )}
                      </p>
                      <div className="flex flex-wrap justify-end gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setPendingQualityAction(null)}
                        >
                          {t('setPrintSize.adjustQualityCta', 'Adjust size')}
                        </Button>
                        <Button type="button" size="sm" onClick={handleAcceptPrintQualityWarning}>
                          {t('setPrintSize.acceptLowPpiCta', 'Use anyway')}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}
              <dl className="mt-4 grid gap-2 text-xs text-muted-foreground sm:text-sm">
                <div className="flex items-center justify-between">
                  <dt className="font-medium text-foreground">
                    {t('setPrintSize.resolutionLabel', 'Resolution')}
                  </dt>
                  <dd>
                    {formatPpi(currentDpiX, currentDpiY) || t('setPrintSize.currentDpiUnavailable', '—')}
                  </dd>
                </div>
              </dl>
            </div>
            <div className="rounded-2xl border border-border bg-card p-4 shadow-sm sm:p-6">
              <p className="mb-3 text-sm font-semibold text-foreground sm:text-base">
                {t('setPrintSize.originalDetails', 'Original details')}
              </p>
              <dl className="grid gap-2 text-sm text-muted-foreground sm:grid-cols-2">
                <div>
                  <dt className="font-medium text-foreground">{t('setPrintSize.pixelsLabel', 'Pixels')}</dt>
                  <dd>
                    {metadata?.width ?? item?.originalWidthPx ?? '—'} × {metadata?.height ?? item?.originalHeightPx ?? '—'}
                  </dd>
                </div>
                <div>
                  <dt className="font-medium text-foreground">{t('setPrintSize.resolutionLabel', 'Resolution')}</dt>
                  <dd>
                    {formatPpi(metadata?.dpiX, metadata?.dpiY)}
                  </dd>
                </div>
                <div>
                  <dt className="font-medium text-foreground">{t('setPrintSize.originalSize', 'Original size')}</dt>
                  <dd>{formatOriginalSize()}</dd>
                </div>
                <div>
                  <dt className="font-medium text-foreground">{t('setPrintSize.fileId', 'File ID')}</dt>
                  <dd className="truncate text-xs text-muted-foreground/80">
                    {item.uploadedFileId ?? '—'}
                  </dd>
                </div>
              </dl>
            </div>
          </section>
        </div>
      </div>
      <div className="mt-4 flex gap-2 sm:hidden">
        <Button variant="outline" className="flex-1" onClick={handleCancel}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button className="flex-1" onClick={handleSave} disabled={isSaveDisabled}>
          {isSaving || isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden /> : null}
          {saveButtonLabel}
        </Button>
      </div>
    </div>
  );
};

export default SetPrintSizeWindow;
